import { Badge, BadgeIcon, BadgeText } from "@/components/ui/badge";
import { Pressable } from "@/components/ui/pressable";
import { DateRange } from "./date.picker.types";
import { useEffect, useMemo, useState } from "react";
import { Calendar } from "lucide-react-native";
import dayjs from "dayjs";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { Heading } from "@/components/ui/heading";
import { CloseIcon, Icon } from "@/components/ui/icon";
import DateTimePicker from "react-native-ui-datepicker";
import { HStack } from "@/components/ui/hstack";
import { Button, ButtonText } from "@/components/ui/button";

export function DatePreset(props: {
  title: string;
  onPress?: () => void;
  isActive?: boolean;
}) {
  return (
    <Pressable onPress={props.onPress}>
      <Badge
        size="lg"
        action="muted"
        className={`items-center gap-2 ${
          props.isActive ? "border-background-950 border" : ""
        }`}
      >
        <BadgeText className="normal-case text-typography-900">
          {props.title}
        </BadgeText>
      </Badge>
    </Pressable>
  );
}

export function DatePickerBadge({
  selectedRange: initialRange,
  onRangeChange,
}: {
  selectedRange: DateRange;
  onRangeChange: (newRange: DateRange) => void;
}) {
  const [isOpened, setIsOpened] = useState(false);
  const [selectedRange, setSelectedRange] = useState<DateRange>(initialRange);
  const presetDays = useMemo(
    () =>
      [365, 30, 7].map((p, i) => {
        let isActive = false;

        if (i === 0 && !selectedRange) {
          isActive = true;
        }

        if (selectedRange) {
          const [start, end] = selectedRange;
          if (end?.isSame(dayjs(), "day")) {
            const diff = end.diff(start, "days", false);
            if (diff === p) {
              isActive = true;
            }
          }
        }

        return {
          days: p,
          isActive,
          title: `Last ${p} days`,
        };
      }),
    [selectedRange]
  );

  useEffect(() => {
    setSelectedRange(initialRange);
  }, [initialRange]);

  return (
    <>
      <Pressable
        onPress={() => {
          setIsOpened(true);
        }}
      >
        <Badge size="lg" action="muted" className="items-center gap-2">
          <BadgeIcon as={Calendar} />
          <BadgeText className="normal-case text-typography-900">
            {presetDays.find((p) => p.isActive)?.title ??
              (!!initialRange
                ? `${initialRange[0].format(
                    "DD/MM/YYYY"
                  )} - ${initialRange[1].format("DD/MM/YYYY")}`
                : "Custom")}
          </BadgeText>
        </Badge>
      </Pressable>
      <Modal
        avoidKeyboard
        useRNModal
        isOpen={isOpened}
        onClose={() => setIsOpened(false)}
      >
        <ModalBackdrop />
        <ModalContent>
          <ModalHeader>
            <Heading>Choose period</Heading>
            <ModalCloseButton>
              <Icon
                as={CloseIcon}
                size="md"
                className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
              />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody className="mb-2">
            <DateTimePicker
              mode="range"
              startDate={selectedRange?.[0]}
              endDate={selectedRange?.[1]}
              onChange={({ startDate, endDate }) =>
                setSelectedRange([
                  startDate as dayjs.Dayjs,
                  endDate as dayjs.Dayjs,
                ])
              }
            />
          </ModalBody>
          <ModalFooter className="flex-col items-start gap-4">
            <HStack className="gap-2">
              {presetDays.map((p, i) => (
                <DatePreset
                  key={p.days}
                  title={p.title}
                  isActive={p.isActive}
                  onPress={() => {
                    setSelectedRange(
                      i === 0
                        ? undefined
                        : [dayjs().subtract(p.days, "days"), dayjs()]
                    );
                  }}
                />
              ))}
            </HStack>
            <Button
              className="w-full gap-2"
              onPress={() => {
                onRangeChange(selectedRange);
                setIsOpened(false);
              }}
            >
              <ButtonText>Apply</ButtonText>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
